import { Component } from '@theme/component';
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
  updateAllHeaderCustomProperties,
} from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** GSQ: cascade mega resetuje stan gdy panel się zamyka (współdzielone z `header-drawer.js`). */
const GSQ_SUBMENU_CLOSE_EVENT = 'gsq:submenu-close';

/**
 * @param {HTMLElement} scope - `.menu-list__submenu` lub inny kontener zawierający `[data-gsq-cascade-mega]`
 */
function dispatchGsqSubmenuClose(scope) {
  if (!(scope instanceof HTMLElement)) return;
  document.dispatchEvent(new CustomEvent(GSQ_SUBMENU_CLOSE_EVENT, { bubbles: false, detail: { scope } }));
}

/** Odsunięcie overlay od góry viewportu — pod announcement bar (`--announcement-bar-height` z theme / pomiar). */
function getAnnouncementBarInsetTop() {
  const fromVar = getComputedStyle(document.documentElement).getPropertyValue('--announcement-bar-height').trim();
  if (fromVar && fromVar !== '0' && fromVar !== '0px') {
    return fromVar;
  }
  const bar = document.querySelector('.announcement-bar');
  if (bar instanceof HTMLElement) {
    const h = Math.round(bar.getBoundingClientRect().height);
    if (h > 0) return `${h}px`;
  }
  return '0px';
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * Keeps `--submenu-height` in sync when cascade columns / promos change panel height.
   *
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * GSQ: delayed `data-active` / `data-mega-open` removal so blend stays `normal` until the panel
   * clip-path close finishes. Clearing early caused a visible `difference` flash on the white panel.
   *
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #pendingActiveClearFallbackTimeout = null;

  /** @type {HTMLElement | null} */
  #pendingActiveClearSubmenu = null;

  /** @type {((event: TransitionEvent) => void) | null} */
  #pendingActiveClearOnTransitionEnd = null;

  /**
   * Bumped on every open/close so stale `finish()` from a previous close cannot drop blend flags
   * while a new open (or close) is in progress.
   *
   * @type {number}
   */
  #menuCloseGeneration = 0;

  /** @type {HTMLElement | null} */
  #megaBackdropEl = null;

  connectedCallback() {
    super.connectedCallback();

    this.#megaBackdropEl = this.querySelector('[data-header-menu-mega-backdrop]');
    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('pointerdown', this.#onDocumentDismissOpenMega, true);
    document.addEventListener('focusin', this.#onDocumentDismissOpenMega, true);
    document.addEventListener('keydown', this.#onDocumentKeydown);

    this.#megaBackdropEl?.addEventListener('pointerdown', this.#onMegaBackdropPointerDown, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointerdown', this.#onDocumentDismissOpenMega, true);
    document.removeEventListener('focusin', this.#onDocumentDismissOpenMega, true);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    this.#cleanupSubmenuResizeObserver();
    this.#cancelPendingActiveClear();
    document.documentElement.removeAttribute('data-gsq-mega-scroll-lock');

    const backdrop = this.#megaBackdropEl;
    backdrop?.removeEventListener('pointerdown', this.#onMegaBackdropPointerDown, true);
    if (backdrop instanceof HTMLElement) {
      backdrop.hidden = true;
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.style.removeProperty('top');
      if (backdrop.parentElement === document.body) {
        this.insertBefore(backdrop, this.firstChild);
      }
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onMegaBackdropPointerDown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-header-menu-mega-backdrop]')) return;
    const anchor = this.#state.activeItem;
    if (!anchor || anchor.getAttribute('aria-expanded') !== 'true') return;
    event.preventDefault();
    event.stopPropagation();
    this.#deactivate(anchor, true);
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
    this.#syncMegaBackdrop();
  }, 100);

  /**
   * Pełnoekranowy backdrop (tylko desktop + otwarte mega), żeby klik „obok” nie trafiał w linki strony.
   */
  #syncMegaBackdrop = () => {
    const el = this.#megaBackdropEl;
    if (!(el instanceof HTMLElement)) return;
    const desktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 750px)').matches;
    const open = this.getAttribute('aria-expanded') === 'true';
    const anchor = this.#state.activeItem;
    const sub = anchor ? findSubmenu(anchor) : null;
    const hasMega = Boolean(sub?.querySelector?.('.mega-menu, [data-gsq-cascade-mega]'));
    const show = desktop && open && hasMega;
    if (show) {
      el.hidden = false;
      el.setAttribute('aria-hidden', 'false');
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
      el.style.top = getAnnouncementBarInsetTop();
    } else {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      el.style.removeProperty('top');
      if (el.parentElement === document.body) {
        this.insertBefore(el, this.firstChild);
      }
    }
  };

  /**
   * Close an open mega when focus/pointer moves to overlays or outside the active item’s bar slot.
   * Sticky row: keep open while interacting anywhere inside the main `.menu-list` (matches prior hover UX).
   *
   * @param {Event} event
   */
  #onDocumentDismissOpenMega = (event) => {
    const anchor = this.#state.activeItem;
    if (!anchor || anchor.getAttribute('aria-expanded') !== 'true') return;

    const target = event.target;
    if (!(target instanceof Node)) return;

    const sub = findSubmenu(anchor);
    if (sub?.contains(target)) return;

    const nav = this.querySelector('.menu-list');
    const isStickyRow = Boolean(anchor.closest('[data-gsq-sticky-mega-bar]'));
    if (isStickyRow) {
      if (nav?.contains(target)) return;
      this.#deactivate(anchor, true);
      return;
    }

    const itemLi = anchor.closest('li');
    if (itemLi?.contains(target)) return;

    this.#deactivate(anchor, true);
  };

  /**
   * Escape zamyka otwarty panel mega menu (`.mega-menu`).
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const anchor = this.#state.activeItem;
    if (!anchor || anchor.getAttribute('aria-expanded') !== 'true') return;
    const sub = findSubmenu(anchor);
    if (!sub?.querySelector?.('.mega-menu')) return;
    event.preventDefault();
    this.#deactivate(anchor, true);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Invalidate any in-flight close (timers still run from a prior `#deactivate`).
    this.#menuCloseGeneration += 1;
    this.#cancelPendingActiveClear();

    // GSQ: separate mega row — don't steal activation when moving across other top-level bar items
    const prevAnchor = this.#state.activeItem;
    if (
      prevAnchor?.closest?.('[data-gsq-sticky-mega-bar]') &&
      prevAnchor.getAttribute('aria-expanded') === 'true' &&
      event instanceof PointerEvent &&
      event.type === 'pointerenter' &&
      item !== prevAnchor
    ) {
      return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      const prevSub = findSubmenu(previouslyActiveItem);
      if (prevSub instanceof HTMLElement) {
        dispatchGsqSubmenuClose(prevSub);
      }
      if (prevSub) delete prevSub.dataset.active;
      previouslyActiveItem.ariaExpanded = 'false';
      this.#releaseMegaBlendState();
    }

    this.#state.activeItem = item;

    this.#cleanupSubmenuResizeObserver();

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Before aria-expanded: `_gsq-blend-header` keys off `data-active`; setting it first avoids a
      // paint where the mega is opening but `.header-section` is still `mix-blend-mode: difference`
      // (noticeable after refresh if layout/CSS vars are not settled yet).
      submenu.dataset.active = '';

      // Blend + content-visibility: kept in sync with `data-active` through close animation (`_gsq-blend-header.scss`).
      this.headerComponent?.setAttribute('data-mega-open', '');
      this.#submenuResizeObserver = new ResizeObserver(() => {
        const h = submenu.offsetHeight;
        if (h > 0) {
          this.headerComponent?.style.setProperty('--submenu-height', `${h}px`);
        }
      });
      this.#submenuResizeObserver.observe(submenu);
    }

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    if (submenu) {
      requestAnimationFrame(() => {
        updateAllHeaderCustomProperties();
      });
    }

    syncGsqMegaMenuScrollLock(submenu);
    this.#syncMegaBackdrop();
  };

  /**
   * Toggle top-level submenu on click (sticky mega row + standard items).
   * @param {MouseEvent} event
   */
  toggleSubmenu = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = findMenuItem(event.target);
    if (!item) return;

    // If the click originated inside the submenu (e.g. a collection link), let it through.
    const realTarget = event.composedPath?.()?.[0];
    const submenu = findSubmenu(item);
    if (realTarget instanceof Node && submenu?.contains(realTarget)) return;

    // Top-level links without a submenu should navigate normally.
    if (!submenu) return;

    event.preventDefault();
    event.stopPropagation();

    if (item === this.#state.activeItem && item.getAttribute('aria-expanded') === 'true') {
      this.#deactivate(item, true);
    } else {
      this.activate(event);
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const rt = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const isMovingToSubmenu =
      rt && (event.type === 'blur' || event.type === 'focusout') && menu?.contains(rt);
    const isMovingToOverflowMenu =
      rt && rt.parentElement?.matches('[slot="overflow"]');
    const activeAnchor = this.#state.activeItem;
    const itemLi = activeAnchor?.closest?.('li');
    const isMovingWithinSameBarItem = rt && itemLi?.contains(rt);

    if (isMovingToSubmenu || isMovingToOverflowMenu || isMovingWithinSameBarItem) return;

    // GSQ: sticky separate mega — keep open while pointer is anywhere in the header (full navbar) or in this mega panel; close only when leaving both (including gaps: relatedTarget null → check elementFromPoint next frame).
    if (
      activeAnchor?.closest?.('[data-gsq-sticky-mega-bar]') &&
      activeAnchor.getAttribute('aria-expanded') === 'true' &&
      event instanceof PointerEvent &&
      event.type === 'pointerleave'
    ) {
      const sub = findSubmenu(activeAnchor);
      const rtOut = event.relatedTarget;
      const header = this.headerComponent;

      if (rtOut instanceof Node) {
        if (sub?.contains(rtOut)) return;
        if (header?.contains(rtOut)) return;
      } else {
        const { clientX: x, clientY: y } = event;
        requestAnimationFrame(() => {
          if (this.#state.activeItem !== activeAnchor) return;
          const over =
            typeof x === 'number' && typeof y === 'number' ? document.elementFromPoint(x, y) : null;
          if (over instanceof Element) {
            const panel = findSubmenu(activeAnchor);
            if (panel?.contains(over)) return;
            if (header?.contains(over)) return;
          }
          this.#deactivate(undefined, false);
        });
        return;
      }
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - When true, close even if the overflow "More" strip is hovered (e.g. overlay opened).
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    const submenu = findSubmenu(item);
    const closeGen = ++this.#menuCloseGeneration;

    this.#state.activeItem = null;

    this.#cleanupSubmenuResizeObserver();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    document.documentElement.removeAttribute('data-gsq-mega-scroll-lock');

    // Remove active state after close animation (clip-path) so layered styles stay stable
    if (submenu) {
      this.#cancelPendingActiveClear();

      const fallbackMs = this.#submenuCloseDelayMs();
      let finished = false;

      const finish = () => {
        if (finished || closeGen !== this.#menuCloseGeneration) return;
        finished = true;
        this.#cancelPendingActiveClear();
        delete submenu.dataset.active;
        this.style.setProperty('--submenu-opacity', '0');
        this.#releaseMegaBlendState();
      };

      /** @param {TransitionEvent} event */
      const onTransitionEnd = (event) => {
        if (event.target !== submenu || event.propertyName !== 'clip-path') return;
        finish();
      };

      this.#pendingActiveClearSubmenu = submenu;
      this.#pendingActiveClearOnTransitionEnd = onTransitionEnd;
      submenu.addEventListener('transitionend', onTransitionEnd);

      if (submenu instanceof HTMLElement) {
        dispatchGsqSubmenuClose(submenu);
      }

      this.#pendingActiveClearFallbackTimeout = setTimeout(finish, fallbackMs);
    } else {
      this.#releaseMegaBlendState();
    }

    this.#syncMegaBackdrop();
  };

  /** Drops `data-mega-open` so `.header-section` can use `mix-blend-mode: difference` again. */
  #releaseMegaBlendState() {
    this.headerComponent?.removeAttribute('data-mega-open');
  }

  #cancelPendingActiveClear() {
    if (this.#pendingActiveClearFallbackTimeout != null) {
      clearTimeout(this.#pendingActiveClearFallbackTimeout);
      this.#pendingActiveClearFallbackTimeout = null;
    }
    const sub = this.#pendingActiveClearSubmenu;
    const handler = this.#pendingActiveClearOnTransitionEnd;
    if (sub && handler) {
      sub.removeEventListener('transitionend', handler);
    }
    this.#pendingActiveClearSubmenu = null;
    this.#pendingActiveClearOnTransitionEnd = null;
  }

  /**
   * Latest moment to clear open-state flags if `transitionend` never fires (reduced motion, quirks).
   * Parses `--submenu-animation-speed` as CSS time (`360ms`, `0.36s`, …).
   *
   * @returns {number}
   */
  #submenuCloseDelayMs() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--submenu-animation-speed').trim();
    const ms = cssTimeToMs(raw);
    return ms > 0 ? ms + 50 : 410;
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupSubmenuResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {string} value
 * @returns {number} duration in milliseconds, or 0 if unparseable
 */
function cssTimeToMs(value) {
  const s = String(value).trim().toLowerCase();
  if (!s) return 0;
  const n = parseFloat(s);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (s.endsWith('ms')) return Math.ceil(n);
  if (s.endsWith('s')) return Math.ceil(n * 1000);
  return Math.ceil(n);
}

/**
 * Blokuje scroll strony, gdy otwarty jest panel mega menu (`.mega-menu` w submenu).
 * Osobny atrybut niż `scroll-lock` (details), żeby nie zdejmować blokady przy zamykaniu megamenu.
 *
 * @param {HTMLElement | null | undefined} submenu
 */
function syncGsqMegaMenuScrollLock(submenu) {
  if (submenu instanceof HTMLElement && submenu.querySelector('.mega-menu')) {
    document.documentElement.setAttribute('data-gsq-mega-scroll-lock', '');
  } else {
    document.documentElement.removeAttribute('data-gsq-mega-scroll-lock');
  }
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreLi = element.closest?.('li[slot="more"]');
  if (moreLi) {
    const overflowLi = moreLi.parentElement?.querySelector?.('li[slot="overflow"]');
    if (overflowLi instanceof HTMLElement) {
      const nested = overflowLi.querySelector('[ref="menuitem"]');
      if (nested instanceof HTMLElement) return nested;
    }
    return null;
  }

  const nested = element.querySelector?.('[ref="menuitem"]');
  if (nested instanceof HTMLElement) return nested;
  const self = element.closest?.('[ref="menuitem"]');
  return self instanceof HTMLElement ? self : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
